if (!customElements.get('meter-input')) {
  customElements.define(
    'meter-input',
    class MeterInput extends HTMLElement {
      static lengthDataCache = new Map();

      get sectionId() {
        return this.getAttribute('data-section-id') || '';
      }

      get meterField() {
        return this.querySelector('[data-meter-input]');
      }

      get wrapper() {
        return this.closest('[data-meter-wrapper]');
      }

      get quantityField() {
        const productForm = this.closest('.product-form[data-section-id]');
        if (!productForm || productForm.dataset.sectionId !== this.sectionId) return null;

        return productForm.querySelector('input.quantity__input[name="quantity"]');
      }

      connectedCallback() {
        this.abortController = new AbortController();
        this.length = null;
        this.syncRequest = 0;

        this.querySelectorAll('button').forEach((button) => {
          button.addEventListener('click', this.onButtonClick.bind(this), {
            signal: this.abortController.signal,
          });
        });
        this.meterField?.addEventListener('change', this.onMeterChange.bind(this), {
          signal: this.abortController.signal,
        });

        this.bindQuantityField();
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
        this.syncForSku(this.dataset.sku);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.variantChangeUnsubscriber?.();
      }

      bindQuantityField() {
        const quantityField = this.quantityField;
        if (!quantityField || quantityField === this.boundQuantityField) return;

        this.boundQuantityField = quantityField;
        quantityField.addEventListener('change', this.onQuantityChange.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onVariantChange({ data: { sectionId, variant } = {} } = {}) {
        if (String(sectionId) !== this.sectionId) return;

        this.bindQuantityField();
        this.syncForSku(variant?.sku);
      }

      normalizeSku(sku) {
        return String(sku || '').trim().toUpperCase();
      }

      async syncForSku(sku) {
        const request = ++this.syncRequest;
        const normalizedSku = this.normalizeSku(sku);

        if (!normalizedSku) {
          this.setLength(null);
          return;
        }

        const lengths = await this.fetchLengthData();
        if (request !== this.syncRequest) return;

        const length = lengths.get(normalizedSku);
        if (!length) {
          this.setLength(null);
          return;
        }

        this.setLength(length);
        this.syncMeterFromQuantity();
      }

      setLength(length) {
        this.length = Number.isFinite(length) && length > 0 ? length : null;
        this.wrapper?.classList.toggle('hidden', !this.length);
      }

      fetchLengthData() {
        const url = this.dataset.lengthJsonUrl;
        if (!url) return Promise.resolve(new Map());

        if (!MeterInput.lengthDataCache.has(url)) {
          const lengthData = fetch(url)
            .then((response) => {
              if (!response.ok) throw new Error(`Wallpaper length request failed: ${response.status}`);
              return response.json();
            })
            .then((items) => {
              const lengths = new Map();
              items.forEach((item) => {
                const sku = this.normalizeSku(item?.SKU);
                const length = Number.parseFloat(item?.Length);
                if (sku && Number.isFinite(length) && length > 0) lengths.set(sku, length);
              });
              return lengths;
            })
            .catch((error) => {
              console.error('Error fetching wallpaper meter lengths:', error);
              return new Map();
            });

          MeterInput.lengthDataCache.set(url, lengthData);
        }

        return MeterInput.lengthDataCache.get(url);
      }

      onButtonClick(event) {
        if (!this.length || !this.meterField) return;

        const currentMeters = Number.parseFloat(this.meterField.value) || 0;
        const meters = event.currentTarget.name === 'plus' ? currentMeters + 1 : Math.max(0, currentMeters - 1);
        this.applyMetersToQuantity(meters);
      }

      onMeterChange() {
        if (!this.length || !this.meterField) return;
        this.applyMetersToQuantity(Number.parseFloat(this.meterField.value) || 0);
      }

      onQuantityChange() {
        this.syncMeterFromQuantity();
      }

      quantityRules() {
        const quantityField = this.quantityField;
        const minimum = Math.max(
          1,
          Number.parseFloat(quantityField?.dataset.min || quantityField?.getAttribute('min')) || 1
        );
        const increment = Math.max(1, Number.parseFloat(quantityField?.getAttribute('step')) || 1);
        const parsedMaximum = Number.parseFloat(
          quantityField?.dataset.max || quantityField?.getAttribute('max')
        );
        const maximum = Number.isFinite(parsedMaximum) ? parsedMaximum : Number.POSITIVE_INFINITY;

        return { minimum, maximum, increment };
      }

      normalizeQuantity(requestedQuantity) {
        const { minimum, maximum, increment } = this.quantityRules();
        const validMaximum = Number.isFinite(maximum)
          ? minimum + Math.max(0, Math.floor((maximum - minimum) / increment)) * increment
          : Number.POSITIVE_INFINITY;
        const clampedRequest = Math.min(Math.max(requestedQuantity, minimum), validMaximum);
        const incrementsFromMinimum = Math.ceil((clampedRequest - minimum) / increment);
        const roundedQuantity = minimum + Math.max(0, incrementsFromMinimum) * increment;

        return Math.min(Math.max(roundedQuantity, minimum), validMaximum);
      }

      applyMetersToQuantity(meters) {
        const quantityField = this.quantityField;
        if (!this.length || !quantityField) return;

        const requestedQuantity = Math.ceil(Math.max(0, meters) / this.length);
        quantityField.value = this.normalizeQuantity(requestedQuantity);
        quantityField.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncMeterFromQuantity();
      }

      syncMeterFromQuantity() {
        const quantityField = this.quantityField;
        if (!this.length || !quantityField || !this.meterField) return;

        const quantity = this.normalizeQuantity(Number.parseFloat(quantityField.value) || 0);
        if (Number.parseFloat(quantityField.value) !== quantity) quantityField.value = quantity;

        const meters = quantity * this.length;
        this.meterField.value = String(Number.parseFloat(meters.toFixed(4)));
      }
    }
  );
}
